import { useState, useRef, useEffect } from 'react';
import { ChevronDown, FolderPlus, Target, RotateCcw } from 'lucide-react';

/**
 * Convert dropdown - collapses Project, Goal, Habit actions into a single dropdown
 * Used in Inbox view to reduce visual clutter
 */
export default function ConvertDropdown({
  onCreateProject,
  onAddAsGoal,
  onAddAsHabit,
  disabled = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = [
    {
      label: 'Project',
      icon: FolderPlus,
      onClick: onCreateProject,
      color: 'text-blue-400',
      bg: 'hover:bg-blue-500/10'
    },
    {
      label: 'Goal',
      icon: Target,
      onClick: onAddAsGoal,
      color: 'text-monthly',
      bg: 'hover:bg-monthly/10'
    },
    {
      label: 'Habit',
      icon: RotateCcw,
      onClick: onAddAsHabit,
      color: 'text-teal-400',
      bg: 'hover:bg-teal-500/10'
    }
  ];

  const handleOptionClick = (option) => {
    option.onClick?.();
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-1 rounded-md border-none cursor-pointer
          transition-all duration-200 px-1.5 py-1
          text-text-secondary bg-surface-hover hover:bg-surface-card hover:text-text-primary
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <span className="text-[10px] whitespace-nowrap">Convert to</span>
        <ChevronDown size={10} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 py-1 bg-surface-card border border-border rounded-lg shadow-lg z-50 min-w-[120px]">
          {options.map((option) => (
            <button
              key={option.label}
              onClick={() => handleOptionClick(option)}
              className={`
                w-full flex items-center gap-2 px-3 py-1.5
                bg-transparent border-none cursor-pointer text-left
                transition-all duration-150
                ${option.color} ${option.bg}
              `}
            >
              <option.icon size={12} />
              <span className="text-[11px]">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
